// Helpers para crear proyectos del Estudio de Video (`video_projects`) desde
// afuera del editor. Ver ADR y docs.
//
// El editor (`VideoEditor.tsx`) NO lee navigation state ni query params: toda la
// personalización de un proyecto se hace en su fila de la DB ANTES de navegar, y
// el editor hidrata sin lógica especial un `state.clips` pre-poblado (solo
// re-firma `storagePath` → `src` al montar). Por eso "crear un proyecto con un
// video ya en la timeline" = crear la fila + copiar el binario al bucket del
// editor + registrar el asset + pre-poblar `state.clips`, y recién ahí navegar.

import { supabase } from '../lib/supabase';
import { registerExistingAsset } from './projectAssets';

// Bucket donde viven los videos del historial (imagenes_generadas).
const HISTORIAL_BUCKET = 'generadas';
// Bucket de assets del editor (video_project_assets siempre resuelve contra este).
const ASSETS_BUCKET = 'video-project-assets';
// Color de la track de video en el editor (TRACK_CONFIG).
const VIDEO_TRACK_COLOR = '#1644EB';

/**
 * Resuelve la marca (`brand_profile_id`) para un proyecto nuevo. Misma lógica
 * que usa "Crear proyecto" en `VideoProjects`:
 *   0) `preferBrandId` explícito (ej. el filtro de marca de la lista).
 *   1) la marca activa del usuario (`profiles.current_brand_id`), validada.
 *   2) la marca default de la empresa (o la no-draft más vieja).
 *   3) si no hay ninguna, crea una default con el nombre de la empresa.
 */
export async function resolveProjectBrandId(
  companyId: string,
  opts?: { preferBrandId?: string | null },
): Promise<string | null> {
  if (opts?.preferBrandId) return opts.preferBrandId;

  // 1) Marca activa del usuario (si sigue perteneciendo a la empresa y no es draft).
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (user) {
    const { data: prof } = await supabase
      .from('profiles')
      .select('current_brand_id')
      .eq('id', user.id)
      .maybeSingle();
    const activeId = (prof as any)?.current_brand_id as string | null;
    if (activeId) {
      const { data: activeBrand } = await supabase
        .from('brand_profiles')
        .select('id')
        .eq('id', activeId)
        .eq('id_company', companyId)
        .eq('is_draft', false)
        .maybeSingle();
      if (activeBrand?.id) return activeBrand.id;
    }
  }

  // 2) Default de la empresa (o la no-draft más vieja).
  const { data: existing } = await supabase
    .from('brand_profiles')
    .select('id')
    .eq('id_company', companyId)
    .eq('is_draft', false)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (existing?.id) return existing.id;

  // 3) Crear una default con el nombre de la empresa.
  const { data: companyRow } = await supabase
    .from('company_profiles')
    .select('nombre')
    .eq('id_company', companyId)
    .maybeSingle();
  const brandName = (companyRow?.nombre || '').trim() || 'Marca principal';
  const { data: created, error: createErr } = await supabase
    .from('brand_profiles')
    .insert({
      id_company: companyId,
      name: brandName,
      context: '',
      colors: [],
      is_default: true,
    })
    .select('id')
    .single();
  if (createErr) console.error('brand_profiles insert failed', createErr);
  return created?.id || null;
}

/**
 * Probea duración + dimensiones de un video a partir de una URL (signed URL).
 * Con timeout de 10s → `null`, por si el `<video>` nunca dispara metadata ni
 * error (codecs raros, CORS, DRM), así `createStudioProjectFromVideo` no se
 * cuelga y cae a los defaults.
 */
function probeVideoFromUrl(
  url: string,
): Promise<{ duration: number; width: number; height: number } | null> {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    let settled = false;
    const finish = (
      v: { duration: number; width: number; height: number } | null,
    ) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(v);
    };
    const timer = window.setTimeout(() => finish(null), 10000);
    el.onloadedmetadata = () =>
      finish({
        duration: Number.isFinite(el.duration) ? el.duration : 0,
        width: el.videoWidth,
        height: el.videoHeight,
      });
    el.onerror = () => finish(null);
    el.src = url;
  });
}

export interface CreateStudioProjectFromVideoParams {
  companyId: string;
  userId?: string | null;
  /** `file_name` del video en el historial (bucket `generadas`, path `${companyId}/full/${fileName}`). */
  fileName?: string;
  /**
   * El video en otro lado del storage (bucket + path completo): un video que el usuario
   * subió al chat y no es una creación. Con esto, `fileName` no hace falta
   * (ADR-2026-10-11-ir-al-estudio-desde-el-chat). Uno de los dos tiene que venir.
   */
  origen?: { bucket: string; path: string };
  /** Nombre para el proyecto + el clip. Default "Video del historial". */
  name?: string;
  /** Duración ya probada (opcional; si falta se probea del signed URL). */
  durationSec?: number;
}

/**
 * Crea un proyecto NUEVO en el Estudio de Video con `fileName` ya puesto como
 * clip de video en la timeline. Devuelve el `projectId` (para navegar a
 * `/editor-video/:id`). Lanza Error si algo falla (y limpia el proyecto huérfano).
 */
export async function createStudioProjectFromVideo(
  params: CreateStudioProjectFromVideoParams,
): Promise<string> {
  const { companyId, userId, fileName } = params;
  const name = (params.name || 'Video del historial').slice(0, 80);
  if (!params.origen && !fileName) throw new Error('No se sabe qué video abrir.');

  // 1) Signed URL del video original (el historial, o el bucket/path que vino).
  const srcBucket = params.origen?.bucket ?? HISTORIAL_BUCKET;
  const srcPath = params.origen?.path ?? `${companyId}/full/${fileName}`;
  const { data: srcSigned, error: srcErr } = await supabase.storage
    .from(srcBucket)
    .createSignedUrl(srcPath, 3600);
  if (srcErr || !srcSigned?.signedUrl) {
    throw new Error('No se pudo leer el video del historial.');
  }

  // 2) Probe duración + dimensiones (para clip.duration y aspectRatio).
  const probed = await probeVideoFromUrl(srcSigned.signedUrl);
  const duration = params.durationSec ?? (probed?.duration || 5);
  const width = probed?.width ?? 0;
  const height = probed?.height ?? 0;
  const aspectRatio = width && height ? (width < height ? '9:16' : '16:9') : '9:16';

  // 3) Crear el proyecto (para tener su id antes de copiar el binario).
  const brandProfileId = await resolveProjectBrandId(companyId);
  const { data: proj, error: projErr } = await supabase
    .from('video_projects')
    .insert({
      id_company: companyId,
      name,
      state: {},
      duration: Math.max(1, Math.ceil(duration)),
      brand_profile_id: brandProfileId,
    })
    .select('id')
    .single();
  if (projErr || !proj) throw new Error('No se pudo crear el proyecto.');
  const projectId = proj.id as string;

  // Path destino declarado ANTES del try para poder limpiar el binario en el
  // catch si la subida ya ocurrió pero un paso posterior falla.
  const dstPath = `${companyId}/${projectId}/${crypto.randomUUID()}.mp4`;

  try {
    // 4) Copiar el binario del historial al bucket del editor (patrón client-side
    //    ya usado en el repo: signed URL → fetch → blob → upload).
    const fetchRes = await fetch(srcSigned.signedUrl);
    // fetch NO tira en 4xx/5xx: sin este check subiríamos un body de error
    // (HTML/JSON) como si fuera el video, y el editor abriría un clip roto.
    if (!fetchRes.ok) {
      throw new Error(`No se pudo descargar el video (${fetchRes.status}).`);
    }
    const blob = await fetchRes.blob();
    const { error: upErr } = await supabase.storage
      .from(ASSETS_BUCKET)
      .upload(dstPath, blob, { contentType: 'video/mp4', upsert: false });
    if (upErr) throw new Error(upErr.message);

    // 5) Registrar el asset (fila en video_project_assets, sin re-subir).
    const asset = await registerExistingAsset({
      projectId,
      companyId,
      userId,
      storagePath: dstPath,
      type: 'video',
      name,
      mimeType: 'video/mp4',
      durationSeconds: duration,
      width: width || null,
      height: height || null,
    });
    if (!asset) throw new Error('No se pudo registrar el asset.');

    // 6) Pre-poblar state.clips con el clip de video + setear la duración.
    const state = {
      clips: [
        {
          id: crypto.randomUUID().slice(0, 8),
          type: 'video',
          name,
          startTime: 0,
          duration,
          sourceDuration: duration,
          color: VIDEO_TRACK_COLOR,
          volume: 1,
          assetId: asset.id,
          storagePath: asset.storage_path,
        },
      ],
      aspectRatio,
    };
    const { error: updErr } = await supabase
      .from('video_projects')
      .update({ state, duration })
      .eq('id', projectId);
    if (updErr) throw new Error(updErr.message);

    return projectId;
  } catch (err) {
    // Limpiar (best-effort) el binario subido + el proyecto huérfano si falló
    // algo después de crearlo. Storage NO borra en cascada por la fila, y si la
    // subida no llegó a ocurrir el remove es un no-op inofensivo.
    try {
      await supabase.storage.from(ASSETS_BUCKET).remove([dstPath]);
    } catch {
      /* best-effort */
    }
    try {
      await supabase.from('video_projects').delete().eq('id', projectId);
    } catch {
      /* best-effort */
    }
    throw err;
  }
}
